import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@mui/material";

import useStyles from "./styles";
import { Sidebar } from "../../containers/Sidebar/Sidebar";
import { CustomTypography } from "../../components/CustomTypography/CustomTypography";
import { colors } from "../../styles/colors";
import { CustomSearch } from "../../components/CustomSearch/CustomSearch";
import { CustomButton } from "../../components/CustomButton/CustomButton";
import { CustomCost } from "../../components/CustomCost/CustomCost";
import { ResultSearchVehicle } from "./ResultSearchVehicle/ResultSearchVehicle";
import { ResultSearchCollab } from "./ResultSearchCollab/ResultSearchCollab";
import { useGetTCOByCompany } from "../../api/tco";
import { useGetAllVehiculesFleet } from "../../api/vehicule";
import { useGetAllEmployeeWithVehicle } from "../../api/employee";
import { getCurrentFleetId } from "../../utils/getCurrentFleet";
import { formatEuros } from "../../utils/money";

type Props = {};

/**
 * TCO — cost of the fleet.
 *
 * This page shipped as a static mock: the two headline figures were the string
 * literals "€ 229,67" and "€ 1224,56", both search boxes were fed `options={[]}`,
 * and the "See the cost" buttons had no onClick at all. Selecting anything in a
 * search field immediately revealed a result panel that then rendered another
 * pair of hardcoded figures, so the page looked alive while showing the same
 * numbers to every customer.
 *
 * Everything below is now driven by the API:
 *   - headline figures      -> /companies/{fleet}/tco-by-company
 *   - per-vehicle result    -> /companies/{fleet}/tco-by-vehicles/{vehicleId}
 *   - per-employee result   -> /companies/{fleet}/tco-by-employee/{userId}
 *
 * The result panels are gated behind the button rather than the search's
 * onChange. Picking an option is not the same gesture as asking for a report,
 * and firing the request on selection meant every keystroke-driven pick hit the
 * API.
 */
export const TCO: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const fleetId = getCurrentFleetId();

  const { tco, isLoading: isLoadingTco } = useGetTCOByCompany(fleetId);

  // 5000 mirrors the ceiling other pickers in the app use: the search is a
  // client-side autocomplete over the whole fleet, not a paged list.
  const { vehicules, isLoading: isLoadingVehicles } = useGetAllVehiculesFleet(
    fleetId,
    1,
    5000
  );
  const { employees, isLoading: isLoadingEmployees } =
    useGetAllEmployeeWithVehicle(fleetId);

  // Two-step state per panel: what is highlighted in the autocomplete, and what
  // the user actually asked to see. Keeping them apart is what stops a request
  // firing on every selection.
  const [pickedVehicleId, setPickedVehicleId] = useState<number | null>(null);
  const [shownVehicleId, setShownVehicleId] = useState<number | null>(null);

  const [pickedUserId, setPickedUserId] = useState<number | null>(null);
  const [shownUserId, setShownUserId] = useState<number | null>(null);

  const shownEmployee = useMemo(
    () => employees.find((e) => e.userId === shownUserId) ?? null,
    [employees, shownUserId]
  );

  const vehicleLabel = (option: any) =>
    [option?.brand, option?.brandModel, option?.immatriculation]
      .filter(Boolean)
      .join(" ")
      .trim() || t("common.notAvailable", "N/A");

  const employeeLabel = (option: any) =>
    [option?.firstname, option?.lastname].filter(Boolean).join(" ").trim() ||
    t("common.notAvailable", "N/A");

  return (
    <Sidebar
      styles={{
        sidebarRightRoot: classes.vehiculeRoot,
        sidebarRoot: classes.contentMain,
      }}
    >
      <div className={classes.containerHeader}>
        <CustomTypography
          variant="h2"
          text={t("fleet.costOfTheFleet", "TCO - Cost of the fleet")}
          isUppercase
        />
      </div>

      <div className={classes.divContent}>
        <div className={classes.divContentTCO}>
          {isLoadingTco ? (
            <>
              <Skeleton variant="rounded" height={90} className={classes.skeletonCard} />
              <Skeleton variant="rounded" height={90} className={classes.skeletonCard} />
            </>
          ) : (
            <>
              <CustomCost
                label={t(
                  "panel.average.averageMonthlyCost",
                  "AVERAGE MONTHLY COST"
                )}
                value={formatEuros(tco?.total_monthly_cost)}
              />
              <CustomCost
                label={t("panel.average.totalCost", "TOTAL COST")}
                value={formatEuros(tco?.total_tco_cost)}
              />
            </>
          )}
        </div>

        <div className={classes.divContentTCO}>
          {/* ---------------- Cost per vehicle ---------------- */}
          <div className={classes.subContainer2}>
            <CustomTypography
              text={t("fleet.costPerVehicle", "Cost per vehicle")}
              variant={"h5"}
              color={colors.primary}
              isUppercase
            />
            <div className={classes.divSearch}>
              <CustomSearch
                options={vehicules}
                getOptionLabel={vehicleLabel}
                isOptionEqualToValue={(option: any, value: any) =>
                  option?.vehicleId === value?.vehicleId
                }
                label={t("fleet.searchForAVehicle", "Search for a vehicle")}
                placeholder={"XX-000-XX AUDI A11"}
                onChange={(_, vehicle: any) => {
                  setPickedVehicleId(vehicle?.vehicleId ?? null);
                }}
                onChangeTextField={(e) => {
                  // Clearing the field must clear the selection, otherwise the
                  // button stays armed with a vehicle no longer displayed.
                  if (e.target.value.trim().length === 0) {
                    setPickedVehicleId(null);
                  }
                }}
              />
              <CustomButton
                label={t("fleet.seeTheCost", "See the cost")}
                variant={pickedVehicleId ? "primary" : "disabled"}
                disabled={!pickedVehicleId || isLoadingVehicles}
                width={"fit-content"}
                height={"40px"}
                onClick={() => setShownVehicleId(pickedVehicleId)}
              />
            </div>
            {shownVehicleId !== null && (
              <ResultSearchVehicle
                vehicleId={shownVehicleId}
                onClose={() => setShownVehicleId(null)}
              />
            )}
          </div>

          {/* ---------------- Cost per collaborator ---------------- */}
          <div className={classes.subContainer2}>
            <CustomTypography
              text={t("fleet.costPerEmployee", "Cost per employee")}
              variant={"h5"}
              color={colors.primary}
              isUppercase
            />
            <div className={classes.divSearch}>
              <CustomSearch
                options={employees}
                getOptionLabel={employeeLabel}
                isOptionEqualToValue={(option: any, value: any) =>
                  option?.userId === value?.userId
                }
                label={t(
                  "fleet.searchForACollaborator",
                  "Search for a collaborator"
                )}
                placeholder={"Thierry Martin"}
                onChange={(_, employee: any) => {
                  setPickedUserId(employee?.userId ?? null);
                }}
                onChangeTextField={(e) => {
                  if (e.target.value.trim().length === 0) {
                    setPickedUserId(null);
                  }
                }}
              />
              <CustomButton
                label={t("fleet.seeTheCost", "See the cost")}
                variant={pickedUserId ? "primary" : "disabled"}
                disabled={!pickedUserId || isLoadingEmployees}
                width={"fit-content"}
                height={"40px"}
                onClick={() => setShownUserId(pickedUserId)}
              />
            </div>
            {shownUserId !== null && (
              <ResultSearchCollab
                userId={shownUserId}
                employeeName={
                  shownEmployee ? employeeLabel(shownEmployee) : undefined
                }
                onClose={() => setShownUserId(null)}
              />
            )}
          </div>
        </div>
      </div>
    </Sidebar>
  );
};
